import React, { ReactNode, useEffect, useRef, useState } from "react";
import { Typography, theme, Button } from "antd";
import SpotIcon from "./SpotIcon";
import { Icons } from "../../assets/icons";
import {
  ProcessItem,
  RoadmapContext,
  useProcessItems,
} from "./useProcessItems";
import RichText from "../ui/RichText";
import { useNavigate } from "react-router-dom";

const colorList = ["green", "red", "purple", "yellow", "green", "purple"];
// Pin positions for up to 6 stages (PD-02 §6.9: Awareness → Policy & Commitment
// → Planning & Assessment → Implementation → Evaluation → Management & Review).
// If the roadmap has fewer stages the extra slots simply aren't rendered.
const positionList = [
  { top: "4%", left: "18%" },
  { top: "28%", left: "38%" },
  { top: "50%", left: "58%" },
  { top: "68%", left: "42%" },
  { top: "58%", left: "72%" },
  { top: "22%", left: "72%" },
];
const textPlace = ["right", "right", "right", "right", "top", "top"];

const isDisabled = (processItems: ProcessItem[], index: number) => {
  if (index === 0) return false;
  return !processItems[index - 1].isCompleted;
};

// The footer is a compact caption — strip any rich-text markup to plain text.
const stripHtml = (html?: string) =>
  (html ?? "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

function Roadmap() {
  const {
    processItems,
    loading,
    error,
    notStarted,
    noRoadmap,
    roadmap,
    start,
    starting,
  } = useProcessItems();
  const navigate = useNavigate();
  const { token } = theme.useToken();

  // Detect the not-started → started transition (the moment the journey is enrolled) and run a
  // one-shot "activation" animation so the map comes alive instead of snapping.
  const [activating, setActivating] = useState(false);
  const prevNotStarted = useRef<boolean | null>(null);
  useEffect(() => {
    if (prevNotStarted.current === true && notStarted === false) {
      setActivating(true);
      const timer = setTimeout(() => setActivating(false), 1600);
      prevNotStarted.current = notStarted;
      return () => clearTimeout(timer);
    }
    prevNotStarted.current = notStarted;
  }, [notStarted]);

  // Find the index of the last completed item
  const lastCompletedIndex =
    processItems.findIndex((item) => !item.isCompleted) - 1;

  const mapCenterIcon = (index: number) => {
    switch (index) {
      case 0:
        return <Icons.AwarnessStageIcon width={20} height={20} />;
      case 1:
        return <Icons.PolicyCommitmentStageIcon width={20} height={20} />;
      case 2:
        return <Icons.PlanningActionsIcon width={20} height={20} />;
      case 3:
        // Implementation — reuse Planning icon until a dedicated one ships
        return <Icons.PlanningActionsIcon width={20} height={20} />;
      case 4:
        return <Icons.EvaluationReportingIcon width={20} height={20} />;
      case 5:
        // Management & Review — reuse Evaluation icon until a dedicated one ships
        return <Icons.EvaluationReportingIcon width={20} height={20} />;
      default:
        return <Icons.AwarnessStageIcon width={20} height={20} />;
    }
  };

  return (
    <div
      className="flex items-center justify-center rounded-lg px-8 pb-16 pt-8"
      style={{
        width: 1200,
        height: 1000,
      }}
    >
      <div className="flex flex-col items-center">
        <Typography.Title
          style={{
            fontFamily: token.fontFamily,
          }}
          level={3}
          className="pb-6 text-center"
        >
          Key Processes Roadmap
        </Typography.Title>
        <RoadmapContainer>
          {loading ? (
            <div className="flex h-full items-center justify-center">
              <Typography.Text
                style={{
                  fontFamily: token.fontFamily,
                }}
              >
                Loading roadmap data...
              </Typography.Text>
            </div>
          ) : error ? (
            <div className="flex h-full items-center justify-center">
              <Typography.Text
                style={{
                  fontFamily: token.fontFamily,
                }}
                type="danger"
              >
                Error loading roadmap: {error.message}
              </Typography.Text>
            </div>
          ) : noRoadmap ? (
            <div className="flex h-full items-center justify-center">
              <Typography.Text style={{ fontFamily: token.fontFamily }}>
                No wellbeing journey has been published yet.
              </Typography.Text>
            </div>
          ) : (
            <>
              {processItems.slice(0, positionList.length).map((item, index) => (
                <div
                  key={item.Title}
                  className={activating ? "animate__animated animate__pulse" : ""}
                  style={{
                    position: "absolute",
                    top: positionList[index].top,
                    left: positionList[index].left,
                    // Stagger the pins so the journey "ripples" alive from the first stage.
                    animationDelay: activating ? `${index * 140}ms` : undefined,
                  }}
                >
                  <SpotIcon
                    color={
                      colorList[index] as "green" | "red" | "purple" | "yellow"
                    }
                    position={
                      textPlace[index] as "right" | "bottom" | "top" | "left"
                    }
                    onClick={() => {
                      navigate(`/processes${processItems[index].componentUrl}`);
                    }}
                    disabled={notStarted || isDisabled(processItems, index)}
                    disabledMessage={
                      notStarted
                        ? "Start the journey to begin"
                        : index > 0
                          ? `Complete "${processItems[index - 1].Title}" first`
                          : ""
                    }
                    isNextAvailable={!notStarted && index === lastCompletedIndex + 1}
                    isCompleted={item.isCompleted}
                    centerIcon={mapCenterIcon(index)}
                  >
                    <div className="items-left flex flex-col justify-center p-8">
                      <Typography.Title
                        style={{
                          fontFamily: token.fontFamily,
                        }}
                        level={5}
                      >
                        {processItems[index]?.Title || "Awareness Stage"}
                      </Typography.Title>
                      <RichText
                        html={processItems[index]?.steps}
                        className="text-sm"
                        style={{ fontFamily: token.fontFamily }}
                      />
                    </div>
                  </SpotIcon>
                </div>
              ))}
              <div
                className="absolute bottom-16 ml-32"
                style={{
                  left: "-5%",
                }}
              >
                <RoadmapText
                  roadmap={roadmap}
                  processItems={processItems}
                  notStarted={notStarted}
                  onStart={start}
                  starting={starting}
                  activating={activating}
                />
              </div>
            </>
          )}
        </RoadmapContainer>
      </div>
    </div>
  );
}

interface RoadmapPropsContainer {
  children?: ReactNode;
}

function RoadmapContainer({ children }: RoadmapPropsContainer) {
  return (
    <div
      className={`roadmap-container`}
      style={{
        position: "relative",
        width: 1150,
        height: 900,
      }}
    >
      {/* Background Image Layer */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Icons.RoadMapIcon width={1150} height={900} />
      </div>

      {/* Content Layer */}
      <div
        style={{
          position: "relative",
          width: 1150,
          height: 900,
          zIndex: 1,
        }}
      >
        {children}
      </div>
    </div>
  );
}

interface RoadmapTextProps {
  readonly roadmap: RoadmapContext | null;
  readonly processItems: ProcessItem[];
  readonly notStarted: boolean;
  readonly onStart: () => void;
  readonly starting: boolean;
  readonly activating: boolean;
}

function RoadmapText({
  roadmap,
  processItems,
  notStarted,
  onStart,
  starting,
  activating,
}: RoadmapTextProps) {
  const { token } = theme.useToken();
  const currentStage = roadmap
    ? processItems.find((p) => p.Level === roadmap.currentStage)
    : undefined;

  return (
    <div className="w-96">
      <Typography.Title
        style={{ fontFamily: token.fontFamily }}
        level={3}
        className="mb-1 text-left"
      >
        {roadmap?.name ?? "Wellbeing Journey"}
      </Typography.Title>

      {notStarted ? (
        <>
          <Typography.Title
            style={{ fontFamily: token.fontFamily }}
            level={4}
            className="mt-0 text-left"
          >
            Not started yet
          </Typography.Title>
          <Typography.Paragraph style={{ fontFamily: token.fontFamily }}>
            {stripHtml(roadmap?.description) ||
              "Begin your company's wellbeing journey to unlock its stages."}
          </Typography.Paragraph>
          <Button type="primary" size="large" loading={starting} onClick={onStart}>
            Start the Journey
          </Button>
        </>
      ) : (
        <div className={activating ? "animate__animated animate__fadeIn" : ""}>
          <Typography.Title
            style={{ fontFamily: token.fontFamily }}
            level={4}
            className="mt-0 text-left"
          >
            {currentStage?.Title ?? "Completed"}
            {roadmap ? ` — Stage ${roadmap.currentStage}/${roadmap.totalStages}` : ""}
          </Typography.Title>
          <Typography.Paragraph
            style={{ fontFamily: token.fontFamily }}
            ellipsis={{ rows: 5 }}
          >
            {stripHtml(currentStage?.overview) || stripHtml(roadmap?.description)}
          </Typography.Paragraph>
        </div>
      )}
    </div>
  );
}

export default Roadmap;
